import React from 'react';
import {
  Button,
  Card,
  Divider,
  Typography,
  Space,
  Flex,
  theme,
} from 'antd';
import {
  X,
  ArrowRight,
  LayoutGrid,
  Video,
  Image as ImageIcon,
  CirclePlay,
  CirclePause,
  Volume2,
  Maximize,
  Move,
  Target,
  ZoomIn,
  Camera,
  Expand,
  MousePointerClick,
} from 'lucide-react';
import Icon from './Icon';
import { alpha } from '../utils/color';

const { Text, Title } = Typography;

const ContentGuide = ({ open, onClose, moduleId }) => {
  const { token } = theme.useToken();

  if (!open) return null;

  return (
    /* Backdrop */
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: alpha(token.colorBgLayout, 0.8),
        backdropFilter: 'blur(8px)',
        opacity: open ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      {/* Centered container */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          zIndex: 99999,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(95%, 800px)',
          maxHeight: '90vh',
          overflow: 'auto',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Card
          bordered
          style={{
            background: `linear-gradient(135deg, ${alpha(token.colorPrimary, 0.06)} 0%, ${alpha(token.colorPrimary, 0.02)} 100%)`,
            borderColor: token.colorBorder,
            borderRadius: token.borderRadiusLG * 1.5,
            overflow: 'hidden',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          styles={{
            body: { padding: 0 },
          }}
        >
          {/* Close Button */}
          <Button
            type="text"
            shape="circle"
            icon={<Icon as={X} />}
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 999999,
              backgroundColor: alpha(token.colorBgLayout, 0.8),
              backdropFilter: 'blur(8px)',
            }}
          />

          {/* Header */}
          <div
            style={{
              borderBottom: `1px solid ${token.colorBorder}`,
              padding: 32,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <Title
              level={3}
              style={{
                fontWeight: 600,
                color: token.colorPrimary,
                marginBottom: 4,
              }}
            >
              Quick Guide
            </Title>
            <Text type="secondary">
              Learn how to navigate and interact with content
            </Text>
          </div>

          {/* Content */}
          <div style={{ padding: 32 }}>
            {/* Interface Guide */}
            <div style={{ marginBottom: 32 }}>
              <Title level={5} style={{ marginBottom: 16, fontWeight: 600 }}>
                Where to find content
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                &#8226; <strong>Topic titles</strong> are shown in the right panel
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                &#8226; <strong>Topic content</strong> appears when you click on each topic
              </Text>
            </div>

            <Divider style={{ margin: '24px 0' }} />

            {/* Content Types Guide */}
            <div style={{ marginBottom: 32 }}>
              <Title level={5} style={{ marginBottom: 16, fontWeight: 600 }}>
                How to interact with content
              </Title>

              {/* 3D Models */}
              <div
                style={{
                  marginBottom: 16,
                  padding: 16,
                  backgroundColor: alpha(token.colorPrimary, 0.05),
                  borderRadius: token.borderRadius,
                }}
              >
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={LayoutGrid} style={{ color: token.colorPrimary }} />
                  <Text strong style={{ fontSize: 14 }}>3D Models</Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={MousePointerClick} size={14} style={{ color: token.colorPrimary }} />
                  <Text type="secondary">
                    Click and drag to rotate the model
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Move} size={14} style={{ color: token.colorPrimary }} />
                  <Text type="secondary">
                    Scroll mouse wheel to zoom in/out
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Target} size={14} style={{ color: token.colorPrimary }} />
                  <Text type="secondary">
                    Click on highlighted hotspots for information
                  </Text>
                </Flex>
                <Flex gap={8} align="center">
                  <Icon as={Camera} size={14} style={{ color: token.colorPrimary }} />
                  <Text type="secondary">
                    Use camera reset button to return to default view
                  </Text>
                </Flex>
              </div>

              {/* Videos */}
              <div
                style={{
                  marginBottom: 16,
                  padding: 16,
                  backgroundColor: alpha(token.colorText, 0.05),
                  borderRadius: token.borderRadius,
                }}
              >
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Video} style={{ color: token.colorText }} />
                  <Text strong style={{ fontSize: 14 }}>Videos</Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={CirclePlay} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary">
                    Click play button to start video
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={CirclePause} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary">
                    Use pause/play controls as needed
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Volume2} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary">
                    Adjust volume with volume slider
                  </Text>
                </Flex>
                <Flex gap={8} align="center">
                  <Icon as={Maximize} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary">
                    Use fullscreen for better viewing experience
                  </Text>
                </Flex>
              </div>

              {/* Images */}
              <div
                style={{
                  marginBottom: 16,
                  padding: 16,
                  backgroundColor: alpha(token.colorSuccess, 0.05),
                  borderRadius: token.borderRadius,
                }}
              >
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={ImageIcon} style={{ color: token.colorSuccess }} />
                  <Text strong style={{ fontSize: 14 }}>Images</Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={ZoomIn} size={14} style={{ color: token.colorSuccess }} />
                  <Text type="secondary">
                    Click on image to zoom in for details
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Move} size={14} style={{ color: token.colorSuccess }} />
                  <Text type="secondary">
                    Drag zoomed image to pan around
                  </Text>
                </Flex>
                <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                  <Icon as={Expand} size={14} style={{ color: token.colorSuccess }} />
                  <Text type="secondary">
                    Double-click to fit image to screen
                  </Text>
                </Flex>
                <Flex gap={8} align="center">
                  <Icon as={Target} size={14} style={{ color: token.colorSuccess }} />
                  <Text type="secondary">
                    Look for numbered annotations (clickable points)
                  </Text>
                </Flex>
              </div>
            </div>

            <Divider style={{ margin: '24px 0' }} />

            {/* Action Button */}
            <Flex justify="center">
              <Button
                type="primary"
                onClick={onClose}
                icon={<Icon as={ArrowRight} />}
                iconPosition="end"
                style={{
                  borderRadius: token.borderRadiusLG,
                  paddingLeft: 24,
                  paddingRight: 24,
                  height: 40,
                }}
              >
                Got it
              </Button>
            </Flex>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ContentGuide;
